import type { Disposition, DecisionKind } from '../types';

const LABELS: Record<Disposition, string> = {
  monitor: 'Monitor',
  simulate: 'Simulate',
  create_option: 'Create option',
  accelerate: 'Accelerate',
  escalate: 'Escalate',
  structural_rewire: 'Structural rewire',
};

// Ordered by intensity: watch → execute the recommended move → rework the spine.
export const DISPOSITION_ORDER: Disposition[] = ['monitor', 'simulate', 'create_option', 'accelerate', 'escalate', 'structural_rewire'];

// The solver's pick — "execute the recommended move". Badged Recommended, never pre-selected.
export const RECOMMENDED_DISPOSITION: Disposition = 'accelerate';

export function dispositionLabel(d: Disposition): string {
  return LABELS[d];
}

export function dispositionConsequence(d: Disposition): string {
  const routeMap: Record<Disposition, string> = {
    monitor: 'watch, no move',
    simulate: 'opens the simulation path',
    create_option: 'drafts a catalogue option',
    accelerate: 'executes the recommended move',
    escalate: 'routes to a higher owner who decides',
    structural_rewire: 'reworks the strategy spine, acting against a standing constraint',
  };
  return routeMap[d];
}

// The disposition (Approve / Modify / Override) is DERIVED from the chosen card, never picked
// separately (spec 11.4 Rule 3): selecting the recommended move is an Approve; any other response
// is a Modify; reworking the spine acts against a standing constraint, so it is an Override.
// Escalate routes the same decision to a higher owner — it is a Modify, distinct from Override.
export function deriveDecision(selected: Disposition, recommended: Disposition = RECOMMENDED_DISPOSITION): DecisionKind {
  if (selected === recommended) return 'approve';
  if (selected === 'structural_rewire') return 'override';
  return 'modify';
}

interface Props {
  selected?: Disposition;
  onSelect?: (d: Disposition) => void;
  recommended?: Disposition;
  disabled?: boolean;
}

export default function DispositionBar({ selected, onSelect, recommended = RECOMMENDED_DISPOSITION, disabled = false }: Props) {
  return (
    <div className="dispo" role="group" aria-label="Response options">
      {DISPOSITION_ORDER.map((d, i) => {
        const isActive = selected === d;
        const isRecommended = d === recommended;
        const consequence = dispositionConsequence(d);
        return (
          <button
            key={d}
            type="button"
            onClick={() => !disabled && onSelect?.(d)}
            className={isActive ? 'on' : ''}
            disabled={disabled}
            aria-pressed={isActive}
            title={`${LABELS[d]}: ${consequence}`}
          >
            <span className="dispo-index">0{i + 1}</span>
            {isRecommended && <span className="dispo-rec">Recommended</span>}
            <span className="dispo-label">{LABELS[d]}</span>
            <span className="dispo-consequence">{consequence}</span>
          </button>
        );
      })}
    </div>
  );
}
